import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { LLMError, LLMCopyResult } from '../models/llm.models';
import { environment } from 'environments/environment';

@Injectable()
export class LLMService {
  private readonly ALLOWED_EXTENSIONS = ['.txt', '.md', '.markdown'];
  private readonly MAX_FILE_SIZE = 1024 * 1024 * 5;
  private readonly REQUEST_TIMEOUT = 10000;

  private cache = new Map<string, { content: string; timestamp: number }>();
  private readonly CACHE_DURATION = 1000 * 60 * 60; // 1 hour

  constructor(private http: HttpClient) {
    window.addEventListener('beforeunload', () => this.clearCache());
  }

  public copyLLMContent(filePath: string): Observable<LLMCopyResult> {
    try {
      this.validateFilePath(filePath);
    } catch (error) {
      return throwError(
        () =>
          new LLMError(`Invalid file path: ${error instanceof Error ? error.message : String(error)}`, 'INVALID_PATH')
      );
    }

    return this.fetchContent(filePath).pipe(
      tap((content) => this.validateContent(content)),
      map((content) => this.copyToClipboard(content, filePath)),
      catchError((error) => this.handleError(error, filePath))
    );
  }

  public fetchLLMContent(filePath: string): Observable<string> {
    try {
      this.validateFilePath(filePath);
    } catch (error) {
      return throwError(
        () =>
          new LLMError(`Invalid file path: ${error instanceof Error ? error.message : String(error)}`, 'INVALID_PATH')
      );
    }

    return this.fetchContent(filePath).pipe(
      tap((content) => this.validateContent(content)),
      catchError((error) => this.handleError(error, filePath))
    );
  }

  private validateFilePath(filePath: string): void {
    if (!filePath || filePath.trim().length === 0) {
      throw new Error('File path cannot be empty');
    }

    const normalized = this.normalizePath(filePath);

    if (normalized.includes('..') || normalized.includes('//') || normalized.includes('\\')) {
      throw new Error('Path traversal not allowed');
    }

    const hasAllowedExtension = this.ALLOWED_EXTENSIONS.some((ext) => normalized.toLowerCase().endsWith(ext));

    if (!hasAllowedExtension) {
      throw new Error(`File extension not allowed. Allowed extensions: ${this.ALLOWED_EXTENSIONS.join(', ')}`);
    }
  }

  private fetchContent(filePath: string): Observable<string> {
    const normalizedPath = this.normalizePath(filePath);

    const cachedEntry = this.getCacheEntry(normalizedPath);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < this.CACHE_DURATION) {
      return new Observable((observer) => {
        setTimeout(() => {
          observer.next(cachedEntry.content);
          observer.complete();
        }, 0);
      });
    }

    const fullUrl = `${environment.appBaseUrl}/${normalizedPath}`;

    return this.http.get(fullUrl, { responseType: 'text' }).pipe(
      tap((content) => {
        this.addCacheEntry(normalizedPath, content);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  private getCacheEntry(path: string): { content: string; timestamp: number } | null {
    return this.cache.get(path) || null;
  }

  private addCacheEntry(path: string, content: string): void {
    this.cache.set(path, {
      content,
      timestamp: Date.now(),
    });
  }

  private clearCache(): void {
    this.cache.clear();
  }

  private validateContent(content: string): void {
    if (!content || content.trim().length === 0) {
      throw new Error('File content is empty');
    }

    if (new Blob([content]).size > this.MAX_FILE_SIZE) {
      throw new Error(`File size exceeds maximum allowed size of ${this.MAX_FILE_SIZE / 1024 / 1024}MB`);
    }
  }

  private copyToClipboard(content: string, filePath: string): LLMCopyResult {
    const byteCount = new Blob([content]).size;
    const filename = this.extractFilename(filePath);

    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(content).catch(() => {
          this.copyUsingTextarea(content);
        });
      } else {
        this.copyUsingTextarea(content);
      }

      return {
        success: true,
        bytesCopied: byteCount,
        filename,
        message: `${filename} copied to clipboard (${this.formatBytes(byteCount)})`,
      };
    } catch (error) {
      throw new Error(`Failed to copy to clipboard: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private copyUsingTextarea(content: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';

    document.body.appendChild(textarea);

    try {
      textarea.select();
      const successful = document.execCommand('copy');
      if (!successful) {
        throw new Error('document.execCommand(copy) returned false');
      }
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private extractFilename(filePath: string): string {
    return filePath.split('/').pop() || 'llm-content';
  }

  private normalizePath(filePath: string): string {
    return filePath.trim().replace(/\\/g, '/').toLowerCase().replace(/\/+/g, '/');
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private handleError(error: unknown, filePath: string): Observable<never> {
    let errorCode = 'UNKNOWN_ERROR';
    let message = 'An unknown error occurred';

    if (error instanceof Error) {
      message = error.message;

      if (error.message.includes('404')) {
        errorCode = 'FILE_NOT_FOUND';
        message = `LLM file not found: ${filePath}`;
      } else if (error.message.includes('403') || error.message.includes('401')) {
        errorCode = 'ACCESS_DENIED';
        message = 'Access denied: You do not have permission to access this file';
      } else if (error.message.includes('timeout')) {
        errorCode = 'REQUEST_TIMEOUT';
        message = 'Request timeout: Server took too long to respond';
      }
    }

    return throwError(() => new LLMError(message, errorCode));
  }
}
